/**
 * Order-page payment summary — passthrough from Backend financial SSOT.
 */
import { resolveLdsBackendFinancialSsot } from "./ldsBackendFinancialSsot.js";
import {
  parseLdsOrderPayments,
  sumLdsPaymentsCapturedStrict,
} from "./ldsSplitPaymentsSsot.js";

export const LDS_ORDER_PAYMENT_SUMMARY_SSOT_TRACE_TAG =
  "[LDS_ORDER_PAYMENT_SUMMARY_SSOT_TRACE]";

const MONEY_TOL = 0.02;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseAuditIntegrity(row) {
  const audit = row?.post_payment_checkout_audit ?? row?.postPaymentCheckoutAudit;
  if (!audit || typeof audit !== "object") return {};
  const integrity = audit.integrity;
  return integrity && typeof integrity === "object" ? integrity : {};
}

function parsePaymentIntegrity(row) {
  const pi = row?.payment_integrity;
  return pi && typeof pi === "object" ? pi : {};
}

/** Legacy remaining fields — trace / mismatch only, never used as displayed remaining. */
export function readLegacyRemainingBalanceFields(order) {
  const audit = parseAuditIntegrity(order);
  const pi = parsePaymentIntegrity(order);
  return {
    audit_remaining_balance: toNumberOrNull(audit.remaining_balance),
    admin_remaining_balance: toNumberOrNull(
      order?.admin_remaining_balance ?? order?.adminRemainingBalance
    ),
    payment_integrity_remaining: toNumberOrNull(
      pi.remaining_balance ?? pi.remainingBalance
    ),
    remaining_balance_ils: toNumberOrNull(
      order?.remaining_balance_ils ?? order?.remainingBalanceIls
    ),
  };
}

/**
 * Confirmed paid sum — payments[] captures first, then ledger confirmed_paid fields.
 * Used only when Backend lds_partial_payment_display.display_paid is absent.
 * @param {object|null|undefined} order
 */
export function resolveLdsConfirmedPaidSumFromPayments(order) {
  if (!order || typeof order !== "object") {
    return { value: 0, source: "empty_order" };
  }

  const payments = parseLdsOrderPayments(order);
  if (payments.length > 0) {
    const fromSlots = sumLdsPaymentsCapturedStrict(payments, order);
    return {
      value: round2(fromSlots ?? 0),
      source: "payments[]:confirmed_captured",
    };
  }

  const audit = parseAuditIntegrity(order);
  const pi = parsePaymentIntegrity(order);

  const candidates = [
    {
      source: "payment_integrity.confirmed_paid_sum_from_payments",
      value: toNumberOrNull(
        pi.confirmed_paid_sum_from_payments ?? pi.confirmedPaidSumFromPayments
      ),
    },
    {
      source: "post_payment_checkout_audit.integrity.confirmed_paid_sum_from_payments",
      value: toNumberOrNull(audit.confirmed_paid_sum_from_payments),
    },
    {
      source: "confirmed_paid_sum_from_payments",
      value: toNumberOrNull(
        order?.confirmed_paid_sum_from_payments ?? order?.confirmedPaidSumFromPayments
      ),
    },
    {
      source: "admin_paid_amount",
      value: toNumberOrNull(order?.admin_paid_amount ?? order?.adminPaidAmount),
    },
    {
      source: "legacy.sumpaid",
      value: toNumberOrNull(order?.sumpaid),
    },
  ];

  for (const c of candidates) {
    if (c.value != null && c.value >= 0) {
      return { value: round2(c.value), source: c.source };
    }
  }

  return { value: 0, source: "default_zero" };
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

/**
 * @param {object|null|undefined} order
 * @param {{ emitTrace?: boolean, totalAmountOverride?: number|null }} [options]
 */
export function resolveLdsOrderPaymentSummarySsot(order, options = {}) {
  const empty = {
    resolvedTotal: null,
    resolvedTotalSource: "none",
    confirmedPaidSumFromPayments: 0,
    paidSource: "empty_order",
    displayedPaid: 0,
    displayedRemaining: null,
    remainingSource: "none",
    legacySumpaid: null,
    legacyRemainingBalance: null,
    legacyAuditRemaining: null,
    mismatchDetected: false,
    mismatchAmount: null,
    hasBackendBlock: false,
    rejectedCandidates: [],
  };

  if (!order || typeof order !== "object") {
    return empty;
  }

  const backend = resolveLdsBackendFinancialSsot(order, {
    emitTrace: false,
    totalAmountOverride: options?.totalAmountOverride,
  });

  const legacy = readLegacyRemainingBalanceFields(order);
  const legacySumpaid = toNumberOrNull(order?.sumpaid);
  const legacyRemainingBalance =
    legacy.audit_remaining_balance ??
    legacy.admin_remaining_balance ??
    legacy.payment_integrity_remaining ??
    legacy.remaining_balance_ils;

  let mismatchDetected = false;
  let mismatchAmount = null;
  if (
    backend.remaining != null &&
    legacyRemainingBalance != null &&
    Math.abs(backend.remaining - legacyRemainingBalance) > MONEY_TOL
  ) {
    mismatchDetected = true;
    mismatchAmount = round2(legacyRemainingBalance - backend.remaining);
  }

  const out = {
    resolvedTotal: backend.total,
    resolvedTotalSource: backend.totalSource,
    confirmedPaidSumFromPayments: backend.paid,
    paidSource: backend.paidSource,
    displayedPaid: backend.paid,
    displayedRemaining: backend.remaining,
    remainingSource: backend.remainingSource,
    legacySumpaid,
    legacyRemainingBalance,
    legacyAuditRemaining: legacy.audit_remaining_balance,
    mismatchDetected,
    mismatchAmount,
    hasBackendBlock: backend.hasBackendBlock,
    rejectedCandidates: backend.rejectedCandidates,
  };

  if (options.emitTrace !== false && isDevRuntime()) {
    try {
      // eslint-disable-next-line no-console
      console.info(LDS_ORDER_PAYMENT_SUMMARY_SSOT_TRACE_TAG, {
        orderId: String(order.id ?? order._id ?? order.orderid ?? ""),
        resolved_total: out.resolvedTotal,
        confirmed_paid_sum_from_payments: out.displayedPaid,
        displayed_remaining: out.displayedRemaining,
        source: `${out.resolvedTotalSource}|${out.paidSource}|${out.remainingSource}`,
        hasBackendBlock: out.hasBackendBlock,
        rejectedCandidates: out.rejectedCandidates,
        mismatch_detected: mismatchDetected,
      });
    } catch {
      /* ignore */
    }
  }

  return out;
}
